import { CSSProperties } from 'react';

type Vector2 = {
  x: number;
  y: number;
};

type ElementType = 'text' | 'container' | 'image';

/**
 * Convert page coordinates to em-based coordinates within the
 * 16:9 coordinate system (16em width × 9em height)
 */
export const pageToEmCoordinates = (
  pageX: number, 
  pageY: number, 
  containerRect: DOMRect
): Vector2 => {
  // Calculate relative position within container
  const relativeX = pageX - containerRect.left;
  const relativeY = pageY - containerRect.top;
  
  // Convert to em using the 16:9 coordinate system (16em width, 9em height)
  const emX = (relativeX / containerRect.width) * 16;
  const emY = (relativeY / containerRect.height) * 9;
  
  // Ensure values are within bounds
  const boundedX = Math.max(0, Math.min(16, emX));
  const boundedY = Math.max(0, Math.min(9, emY));
  
  return { x: boundedX, y: boundedY };
};

/**
 * Apply snapping to em coordinates
 */
export const applyEmSnapping = (
  position: Vector2,
  threshold: number = 0.5,
  snapPoints: Array<{ x?: number, y?: number }> = [
    { x: 0 }, { x: 4 }, { x: 8 }, { x: 12 }, { x: 16 }, // Horizontal snap points at 0%, 25%, 50%, 75%, 100%
    { y: 0 }, { y: 2.25 }, { y: 4.5 }, { y: 6.75 }, { y: 9 } // Vertical snap points at 0%, 25%, 50%, 75%, 100%
  ]
): { x: number, y: number, snappedX: boolean, snappedY: boolean } => {
  let finalX = position.x;
  let finalY = position.y;
  let snappedX = false;
  let snappedY = false;
  
  // Apply snapping logic for X coordinates
  for (const point of snapPoints) {
    if (point.x !== undefined) {
      const distance = Math.abs(position.x - point.x);
      if (distance < threshold) {
        finalX = point.x;
        snappedX = true;
        break;
      }
    }
  }
  
  // Apply snapping logic for Y coordinates
  for (const point of snapPoints) {
    if (point.y !== undefined) {
      const distance = Math.abs(position.y - point.y);
      if (distance < threshold) {
        finalY = point.y;
        snappedY = true;
        break;
      }
    }
  }
  
  return { x: finalX, y: finalY, snappedX, snappedY };
};

/**
 * Update any element's position using em units
 */
export const updateElementPositionEm = (
  element: any,
  position: Vector2,
  elementType: ElementType
): any => {
  const existingStyle = element.style || {};
  
  // For text elements, position is now the center point directly
  const updatedStyle = {
    ...existingStyle,
    left: `${position.x}em`,
    top: `${position.y}em`,
  };
  
  // Only containers keep their transform for centering
  if (elementType === 'container') {
    updatedStyle.transform = 'translate(-50%, -50%)';
  } else if (elementType === 'image') {
    // Only preserve non-positioning transforms for images
    if (existingStyle.transform?.includes('scale')) {
      const scaleMatch = existingStyle.transform.match(/scale\([^)]+\)/);
      if (scaleMatch) {
        updatedStyle.transform = scaleMatch[0];
      } else {
        delete updatedStyle.transform;
      }
    } else {
      delete updatedStyle.transform;
    }
  } else if (elementType === 'text') {
    // Remove any transform for text elements
    delete updatedStyle.transform;
  }
  
  return {
    ...element,
    style: updatedStyle
  };
};

/**
 * Convert existing percentage-based positions to em-based positions
 * for migrating existing elements
 */
export const convertPercentToEmPosition = (element: any): any => {
  if (!element || !element.style) return element;
  
  const style = { ...element.style };
  let left = style.left;
  let top = style.top;
  
  // If the element already has em-based positioning, preserve it exactly
  if (left?.toString().includes('em') && top?.toString().includes('em')) {
    return element;
  }
  
  // Convert percentage positions to em
  if (left && typeof left === 'string' && left.includes('%')) {
    const leftPercent = parseFloat(left);
    left = `${(leftPercent / 100) * 16}em`;
  }
  
  if (top && typeof top === 'string' && top.includes('%')) {
    const topPercent = parseFloat(top);
    top = `${(topPercent / 100) * 9}em`;
  }
  
  // For text elements, we want to preserve the center position
  if (element.type === 'text') {
    return {
      ...element,
      style: {
        ...style,
        left,
        top,
      }
    };
  }
  
  // For other elements, handle additional properties
  let width = style.width;
  let height = style.height;
  let borderRadius = style.borderRadius;
  
  // Convert percentage dimensions to em for non-text elements
  if (width && typeof width === 'string' && width.includes('%')) {
    const widthPercent = parseFloat(width);
    width = `${(widthPercent / 100) * 16}em`;
  }
  
  if (height && typeof height === 'string' && height.includes('%')) {
    const heightPercent = parseFloat(height);
    height = `${(heightPercent / 100) * 9}em`;
  }
  
  // Convert border radius if needed
  if (borderRadius !== undefined && !String(borderRadius).includes('em')) {
    const radiusValue = parseFloat(String(borderRadius));
    if (!isNaN(radiusValue)) {
      if (String(borderRadius).includes('%')) {
        const radiusPercent = radiusValue;
        const widthEm = typeof width === 'string' && width.includes('em') 
          ? parseFloat(width) 
          : 8;
        const heightEm = typeof height === 'string' && height.includes('em')
          ? parseFloat(height)
          : 4.5;
        
        const minDimension = Math.min(widthEm, heightEm);
        borderRadius = `${(radiusPercent / 100) * minDimension}em`;
      } else {
        borderRadius = `${radiusValue / 16}em`;
      }
    }
  }
  
  return {
    ...element,
    style: {
      ...style,
      left,
      top,
      width,
      height,
      borderRadius
    }
  };
};

/**
 * Generate snap points in em units
 */
export const generateEmSnapPoints = (
  gridSize: number = 4
): Array<{ x?: number, y?: number }> => {
  const snapPoints = [];
  
  // Create horizontal snap points
  for (let x = 0; x <= 16; x += 16/gridSize) {
    snapPoints.push({ x });
  }
  
  // Create vertical snap points
  for (let y = 0; y <= 9; y += 9/gridSize) {
    snapPoints.push({ y });
  }
  
  return snapPoints;
};

/**
 * Get element position from em-based style
 */
export const getElementEmPosition = (style: CSSProperties): Vector2 => {
  let x = 8; // Default to center
  let y = 4.5; // Default to center
  
  if (style.left) {
    const leftValue = style.left.toString();
    if (leftValue.includes('em')) {
      x = parseFloat(leftValue);
    }
  }
  
  if (style.top) {
    const topValue = style.top.toString();
    if (topValue.includes('em')) {
      y = parseFloat(topValue);
    }
  }
  
  return { x, y };
}; 